"use client"

import * as React from "react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Checkbox } from "@/components/ui/checkbox"
import {
  isCategoryVisibleInSearch,
  type ExamCategory,
  type ExamCategoryTreeNode,
} from "@/lib/exam-categories"
import { cn } from "@/lib/utils"

function filterTree(
  nodes: ExamCategoryTreeNode[],
  categories: ExamCategory[],
  query: string,
): ExamCategoryTreeNode[] {
  const trimmed = query.trim()
  if (!trimmed) return nodes
  return nodes
    .map((node) => ({
      ...node,
      children: filterTree(node.children, categories, trimmed),
    }))
    .filter(
      (node) =>
        isCategoryVisibleInSearch(categories, node.id, trimmed) ||
        node.children.length > 0,
    )
}

function isBranchOpen(
  nodeId: string,
  depth: number,
  expandedIds: Record<string, boolean>,
): boolean {
  if (nodeId in expandedIds) return expandedIds[nodeId]!
  return depth === 0
}

function CategoryTreeItem({
  node,
  depth,
  selectedId,
  selectedIds,
  showCheckbox,
  expandedIds,
  onToggleExpand,
  onSelect,
}: {
  node: ExamCategoryTreeNode
  depth: number
  selectedId: string | null
  selectedIds?: string[]
  showCheckbox?: boolean
  expandedIds: Record<string, boolean>
  onToggleExpand: (id: string, open: boolean) => void
  onSelect: (id: string) => void
}) {
  const children = node.children
  const hasChildren = children.length > 0
  const isOpen = isBranchOpen(node.id, depth, expandedIds)
  const isSelected = selectedIds
    ? selectedIds.includes(node.id)
    : selectedId === node.id
  const icon = hasChildren ? (isOpen ? "fa-folder-open" : "fa-folder") : "fa-tag"
  const selectControl = showCheckbox ? (
    <div className="flex size-8 shrink-0 items-center justify-center pe-0.5">
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => onSelect(node.id)}
        aria-label={`Select ${node.name}`}
      />
    </div>
  ) : null

  if (!hasChildren) {
    return (
      <li className="list-none">
        <div
          className={cn(
            "flex min-h-8 w-full min-w-0 items-center rounded-md",
            isSelected
              ? "bg-accent font-medium text-accent-foreground"
              : "text-foreground hover:bg-muted/50",
          )}
          style={{ paddingInlineStart: `calc(0.25rem + ${depth} * 1rem)` }}
        >
          <span className="size-8 shrink-0" aria-hidden />
          <button
            type="button"
            onClick={() => onSelect(node.id)}
            aria-current={isSelected ? "true" : undefined}
            className={cn(
              "flex min-h-8 min-w-0 flex-1 items-center gap-2 py-1.5 pe-3 text-start text-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
            )}
          >
            <i
              className={cn(
                "fa-light shrink-0 text-sm",
                icon,
                depth === 0 || hasChildren ? "text-brand" : "text-muted-foreground",
                isSelected && "fa-solid",
              )}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate">{node.name}</span>
          </button>
          {selectControl}
        </div>
      </li>
    )
  }

  return (
    <li className="list-none">
      <Collapsible
        open={isOpen}
        onOpenChange={(open) => onToggleExpand(node.id, open)}
        className="group/collapsible"
      >
        <div
          className={cn(
            "flex min-h-8 items-center rounded-md",
            isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted/50",
          )}
          style={{ paddingInlineStart: `calc(0.25rem + ${depth} * 1rem)` }}
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
              aria-expanded={isOpen}
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground",
                "hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "data-[state=open]:bg-transparent data-[state=open]:text-muted-foreground",
              )}
            >
              <i
                className="fa-light fa-chevron-right text-xs transition-transform duration-150 group-data-[state=open]/collapsible:rotate-90"
                aria-hidden="true"
              />
            </button>
          </CollapsibleTrigger>

          <button
            type="button"
            onClick={() => onSelect(node.id)}
            aria-current={isSelected ? "true" : undefined}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 py-1.5 pe-3 text-start text-sm",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
              isSelected ? "font-medium" : "text-foreground",
            )}
          >
            <i
              className={cn(
                "fa-light shrink-0 text-sm",
                icon,
                depth === 0 || hasChildren ? "text-brand" : "text-muted-foreground",
                isSelected && "fa-solid",
              )}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1 truncate">{node.name}</span>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
              {children.length}
            </span>
          </button>
          {selectControl}
        </div>

        <CollapsibleContent className="overflow-hidden">
          <ul className="flex flex-col gap-0" role="group" aria-label={`${node.name} subcategories`}>
            {children.map((child) => (
              <CategoryTreeItem
                key={child.id}
                node={child}
                depth={depth + 1}
                selectedId={selectedId}
                selectedIds={selectedIds}
                showCheckbox={showCheckbox}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                onSelect={onSelect}
              />
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </li>
  )
}

export function ExamCategoryTree({
  tree,
  categories,
  selectedId,
  selectedIds,
  showCheckbox = false,
  expandedIds,
  searchQuery = "",
  onToggleExpand,
  onSelect,
}: {
  tree: ExamCategoryTreeNode[]
  categories: ExamCategory[]
  selectedId?: string | null
  selectedIds?: string[]
  showCheckbox?: boolean
  expandedIds: Record<string, boolean>
  searchQuery?: string
  onToggleExpand: (id: string, open: boolean) => void
  onSelect: (id: string) => void
}) {
  const filteredTree = React.useMemo(
    () => filterTree(tree, categories, searchQuery),
    [categories, searchQuery, tree],
  )

  return (
    <ul role="tree" aria-label="Category tree" className="flex flex-col gap-0">
      {filteredTree.length === 0 ? (
        <li className="list-none px-2 py-4 text-sm text-muted-foreground">
          No categories match.
        </li>
      ) : (
        filteredTree.map((node) => (
          <CategoryTreeItem
            key={node.id}
            node={node}
            depth={0}
            selectedId={selectedId ?? null}
            selectedIds={selectedIds}
            showCheckbox={showCheckbox}
            expandedIds={expandedIds}
            onToggleExpand={onToggleExpand}
            onSelect={onSelect}
          />
        ))
      )}
    </ul>
  )
}
